/**
 * Sidebar menu filtering by organisation licensed modules (`userData.Modules`).
 * Layer 1: org module access (same licence set as Role & Permission cards).
 * Layer 2: user permissions (handled in Sidebar.js).
 */

import {
  organizationHasAnalyticsModule,
  organizationHasCapaModule,
  organizationHasEPlannerModule,
  organizationHasESourceModule,
  organizationHasEpsfModule,
  organizationHasIsfModule,
  organizationHasParticipantManagerModule,
  organizationHasQmsModule,
  organizationHasStudyUserManagementModule,
} from "./orgModuleAccess";

/** @typedef {'global' | 'studyUser' | 'isf' | 'epsf' | 'capa' | 'qms' | 'ePlanner' | 'participantManager' | 'analytics' | 'esource'} SidebarOrgModuleKey */

/** Top-level sidebar group for organisation admin screens. */
export const SIDEBAR_ADMIN_MENU_TITLE = "Admin";

export const SIDEBAR_ORG_MODULES = {
  GLOBAL: "global",
  STUDY_USER: "studyUser",
  ISF: "isf",
  EPSF: "epsf",
  CAPA: "capa",
  QMS: "qms",
  EPLANNER: "ePlanner",
  PARTICIPANT_MANAGER: "participantManager",
  ANALYTICS: "analytics",
  ESOURCE: "esource",
};

/** Whether the logged-in organisation has a product module (ignores user role). */
export function organizationHasSidebarOrgModule(userData, orgModuleKey) {
  if (!orgModuleKey || orgModuleKey === SIDEBAR_ORG_MODULES.GLOBAL) {
    return true;
  }

  switch (orgModuleKey) {
    case SIDEBAR_ORG_MODULES.STUDY_USER:
      return organizationHasStudyUserManagementModule(userData);
    case SIDEBAR_ORG_MODULES.ISF:
      return organizationHasIsfModule(userData);
    case SIDEBAR_ORG_MODULES.EPSF:
      return organizationHasEpsfModule(userData);
    case SIDEBAR_ORG_MODULES.CAPA:
      return organizationHasCapaModule(userData);
    case SIDEBAR_ORG_MODULES.ESOURCE:
      return organizationHasESourceModule(userData);
    case SIDEBAR_ORG_MODULES.QMS:
      return organizationHasQmsModule(userData);
    case SIDEBAR_ORG_MODULES.EPLANNER:
      return organizationHasEPlannerModule(userData);
    case SIDEBAR_ORG_MODULES.PARTICIPANT_MANAGER:
      return organizationHasParticipantManagerModule(userData);
    case SIDEBAR_ORG_MODULES.ANALYTICS:
      return organizationHasAnalyticsModule(userData);
    default:
      return true;
  }
}

function subItemAllowedForOrg(subItem, userData, menu) {
  const key =
    subItem?.orgModule ||
    (menu?.orgModule && menu.orgModule !== SIDEBAR_ORG_MODULES.GLOBAL
      ? menu.orgModule
      : null);
  if (!key || key === SIDEBAR_ORG_MODULES.GLOBAL) return true;
  return organizationHasSidebarOrgModule(userData, key);
}

/** Org-module gate for sidebar sub-items (inherits parent menu licence when needed). */
export function subItemAllowedForOrgWithMenu(subItem, menu, userData) {
  return subItemAllowedForOrg(subItem, userData, menu);
}

/**
 * Filter sidebar menus by organisation licensed modules (`userData.Modules`).
 * Layer 1: org module access (same licence set as Role & Permission cards).
 * Layer 2: user permissions (handled in Sidebar.js).
 *
 * Customer System Administrator: full permissions *within* licensed modules only —
 * unlicensed product menus stay hidden (matches User Profile permission cards).
 *
 * @param {Array} menuItems
 * @param {object} userData
 * @param {{ systemAdministrator?: boolean, isSuperAdmin?: boolean }} [options]
 */
export function filterSidebarMenuByOrgModules(menuItems, userData, options = {}) {
  const isSuperAdmin = options.isSuperAdmin === true;

  return (menuItems || [])
    .filter((menu) => {
      if (menu.orgModule) {
        return organizationHasSidebarOrgModule(userData, menu.orgModule);
      }
      if (menu.requiresQmsModule) {
        return organizationHasQmsModule(userData);
      }
      return true;
    })
    .map((menu) => {
      if (isSuperAdmin && menu.title === SIDEBAR_ADMIN_MENU_TITLE) {
        return {
          ...menu,
          subItems: (menu.subItems || []).filter((sub) => !!sub.superAdminOnly),
        };
      }

      // Licensed menu only: keep sub-items allowed for this org.
      // System Administrator still gets all permission keys in Sidebar.js
      // for items that remain after this org filter.
      return {
        ...menu,
        subItems: (menu.subItems || []).filter((sub) =>
          subItemAllowedForOrg(sub, userData, menu),
        ),
      };
    })
    .filter((menu) => (menu.subItems || []).length > 0);
}
